import React from 'react'
import { useTranslation } from 'react-i18next'

import { Col, Row } from 'antd'
import { Formik } from 'formik'
import moment from 'moment'
import * as Yup from 'yup'

import CheckBox from 'common/components/dataEntry/formik/FormikCheckboxField'
import Radio from 'common/components/dataEntry/formik/FormikRadioField'
import Input from 'common/components/dataEntry/formik/FormikStyledInputField'
import { numberOnlyRegx } from 'common/components/utils/Regx'

import { parseZoneFormat } from 'common/dateFormatters'

import Error from '../../common/components/dataEntry/formik/FormItemError'

export const achFormValidator = (translate: any) => {
  return Yup.object({
    bankAccountType: Yup.string().required(translate('general.required')).typeError(translate('general.required')),
    agreeTerms: Yup.bool().oneOf([true], translate('general.required')),
    bankRoutingNumber: Yup.string()
      .min(9, 'Bank routing number should be at least 9 digits')
      .max(12, 'Bank routing number should be at most 12 digits')
      .matches(numberOnlyRegx, 'Bank routing number should contain only numbers')
      .required(translate('general.required')),
    accountNumber: Yup.string()
      .min(5, 'Account number should be at least 5 digits')
      .max(17, 'Account number should be at most 17 digits')
      .required(translate('general.required'))
      .matches(numberOnlyRegx, translate('admin-signup.subscription.errors.account-number-regex')),
    accountNumberConfirmation: Yup.string()
      .oneOf([Yup.ref('accountNumber')], translate('admin-signup.subscription.errors.account-number-confirmation'))
      .required(translate('general.required'))
      .typeError(translate('general.required'))
      .matches(numberOnlyRegx, translate('admin-signup.subscription.errors.account-number-regex')),
    nameOnAccount: Yup.string().trim().required(translate('general.required')),
    billingEmail: Yup.string()
      .nullable()
      .email(translate('admin-signup.subscription.errors.email'))
      .required(translate('general.required')),
  })
}

interface ACHPaymentProps {
  setACHFormComplete: (value: boolean) => void
  setACHPaymentValues: (values: ACHFormValues) => void
  ACHValues: ACHFormValues
  hasDirtyData: (dirtyData: boolean) => void
  recurlyErrors: Map<string, string>
}

export interface ACHFormValues {
  bankAccountType: string
  bankRoutingNumber: string
  accountNumber: string
  accountNumberConfirmation: string
  nameOnAccount: string
  billingEmail: string
  agreeTerms: boolean
}

const ACHPayment: React.FC<ACHPaymentProps> = ({
  setACHFormComplete,
  setACHPaymentValues,
  ACHValues,
  hasDirtyData,
  recurlyErrors,
}) => {
  const { t: translate } = useTranslation()
  return (
    <Formik
      enableReinitialize={true}
      validationSchema={achFormValidator(translate)}
      initialValues={ACHValues}
      onSubmit={() => {}}
      validate={(values: ACHFormValues) => {
        hasDirtyData(true)
        setACHFormComplete(achFormValidator(translate).isValidSync(values))
        setACHPaymentValues(values)
        return {}
      }}
    >
      {(formik) => (
        <form onSubmit={formik.handleSubmit} autoComplete="off" className={'t-admin-signup-ach-payment'}>
          <Row>
            <Col span={24}>
              <div className="formik-field pl-8">
                <label className="formik-field__input-label no-select hand-on-hover">
                  {translate('admin-signup.subscription.payment.bank-account-type-label')}
                </label>
              </div>
            </Col>
          </Row>
          <Row>
            <Col xs={24} md={24}>
              <Radio.Group name="bankAccountType" defaultValue={ACHValues.bankAccountType}>
                <Row gutter={10}>
                  <Col xs={12} sm={8}>
                    <Radio.Button value="checking">
                      {translate('admin-signup.subscription.payment.checking-label')}
                    </Radio.Button>
                  </Col>
                  <Col xs={12} sm={8}>
                    <Radio.Button value="savings">
                      {translate('admin-signup.subscription.payment.saving-label')}
                    </Radio.Button>
                  </Col>
                </Row>
              </Radio.Group>
            </Col>
          </Row>
          {recurlyErrors.get('account_type') ? <Error>{recurlyErrors.get('account_type')}</Error> : null}

          <Row className="form__row">
            <Col md={24}>
              <Input
                label={translate('admin-signup.subscription.payment.bank-routing-number-label')}
                name="bankRoutingNumber"
              />
              {recurlyErrors.get('routing_number') ? <Error>{recurlyErrors.get('routing_number')}</Error> : null}
            </Col>
          </Row>

          <Row className="form__row">
            <Col md={24}>
              <Input label={translate('admin-signup.subscription.payment.account-number-label')} name="accountNumber" />
              {recurlyErrors.get('account_number') ? <Error>{recurlyErrors.get('account_number')}</Error> : null}
            </Col>
          </Row>
          <Row className="form__row">
            <Col md={24}>
              <Input
                label={translate('admin-signup.subscription.payment.confirm-account-number-label')}
                name="accountNumberConfirmation"
              />
              {recurlyErrors.get('account_number_confirmation') ? (
                <Error>{recurlyErrors.get('account_number_confirmation')}</Error>
              ) : null}
            </Col>
          </Row>
          <Row className="form__row">
            <Col md={24}>
              <Input label={translate('admin-signup.subscription.payment.account-name-label')} name="nameOnAccount" />
              {recurlyErrors.get('name_on_account') ? <Error>{recurlyErrors.get('name_on_account')}</Error> : null}
            </Col>
          </Row>
          <Row className="form__row">
            <Col md={24}>
              <Input
                label={translate('admin-signup.subscription.payment.billing-email-label')}
                name="billingEmail"
                placeholder={translate('admin-signup.subscription.payment.billing-email-placeholder')}
              />
            </Col>
          </Row>
          <Row className="form__row">
            <div className="info-card">
              <p>
                {translate('admin-signup.subscription.payment.agreement-1').replace(
                  '[CurrentDate]',
                  parseZoneFormat(moment()),
                )}
              </p>
              <p>{translate('admin-signup.subscription.payment.agreement-2')}</p>
              <p>
                {translate('admin-signup.subscription.payment.agreement-3').replace(
                  '[CurrentDate]',
                  parseZoneFormat(moment()),
                )}
              </p>
            </div>
          </Row>
          <Row className="form__row">
            <Col md={24}>
              <CheckBox name="agreeTerms" className="no-select">
                {translate('admin-signup.subscription.payment.accept-label')}
              </CheckBox>
            </Col>
          </Row>
        </form>
      )}
    </Formik>
  )
}

export default ACHPayment
